import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { TOOL_PRICING } from '../../mocks/tool-pricing';
import { TOOL_LOGOS } from '../../mocks/tool-logos';
import { useCompare } from '../../context/CompareContext';

interface Tool {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  logo: string;
  website: string;
  badge?: 'featured' | 'trending' | 'new';
}

interface ToolCardProps {
  tool: Tool;
}

/** Returns up to 2 uppercase initials from a tool name */
function getInitials(name: string): string {
  const words = name.replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export default function ToolCard({ tool }: ToolCardProps) {
  const [logoError, setLogoError] = useState(false);
  const { toggleTool, isInCompare, isFull } = useCompare();
  const isSelected = isInCompare(tool.slug);

  // Prefer the pre-resolved mapping; fall back to nothing (shows initials)
  const logoUrl = TOOL_LOGOS[tool.slug] ?? '';

  const handleLogoError = useCallback(() => {
    setLogoError(true);
  }, []);

  const getBadgeStyles = (badge: string) => {
    switch (badge) {
      case 'featured': return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
      case 'trending': return 'bg-rose-500/20 text-rose-300 border-rose-500/40';
      case 'new':      return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
      default:         return '';
    }
  };

  const getBadgeIcon = (badge: string) => {
    switch (badge) {
      case 'featured': return <i className="ri-star-line text-xs"></i>;
      case 'trending': return <i className="ri-fire-line text-xs"></i>;
      case 'new':      return <i className="ri-sparkling-line text-xs"></i>;
      default:         return null;
    }
  };

  const getBadgeText = (badge: string) => {
    switch (badge) {
      case 'featured': return 'Featured';
      case 'trending': return 'Trending';
      case 'new':      return 'New Tool';
      default:         return '';
    }
  };

  const pricingType = TOOL_PRICING[tool.slug];

  const getPricingBadgeStyles = (type: string) => {
    switch (type) {
      case 'Free':      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
      case 'Freemium':  return 'bg-teal-500/15 text-teal-300 border-teal-500/30';
      case 'Paid':      return 'bg-orange-500/15 text-orange-300 border-orange-500/30';
      default:          return '';
    }
  };

  const showLogo = logoUrl && !logoError;

  return (
    <div className="relative">
      {/* Compare toggle — outside Link so nesting stays valid */}
      <button
        onClick={() => { if (!isFull || isSelected) toggleTool(tool.slug); }}
        title={isSelected ? 'Remove from compare' : isFull ? 'Compare list full (max 3)' : 'Add to compare'}
        className={`absolute top-3.5 left-4 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer border shrink-0 ${
          isSelected
            ? 'bg-teal-500 border-teal-400 text-white shadow-md shadow-teal-500/40 scale-110'
            : isFull
              ? 'bg-gray-800/80 border-gray-700/40 text-gray-600 cursor-not-allowed opacity-40'
              : 'bg-gray-800/90 border-gray-600/60 text-gray-400 hover:border-teal-500/60 hover:text-teal-400 hover:bg-gray-700/90'
        }`}
      >
        {isSelected
          ? <i className="ri-check-line text-xs font-bold"></i>
          : <i className="ri-add-line text-xs"></i>
        }
      </button>

      <Link
        to={`/tool/${tool.slug}`}
        className={`relative bg-[#111827] backdrop-blur-sm rounded-2xl border transition-all duration-300 flex flex-col h-full group hover:shadow-[0_0_30px_rgba(20,184,166,0.15)] hover:scale-[1.02] overflow-hidden cursor-pointer block ${
          isSelected
            ? 'border-teal-500/50 shadow-[0_0_20px_rgba(20,184,166,0.18)]'
            : 'border-gray-800/80 hover:border-teal-500/40'
        }`}
      >
        {/* Top shimmer line on hover */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

        {/* Badge row */}
        <div className="flex items-center justify-end px-5 pt-4 min-h-[32px]">
          {tool.badge && (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getBadgeStyles(tool.badge)}`}>
              {getBadgeIcon(tool.badge)}
              {getBadgeText(tool.badge)}
            </span>
          )}
        </div>

        <div className="px-5 pb-5 flex flex-col flex-1">
          {/* Logo + name */}
          <div className="flex items-center gap-4 mb-3">
            {/* 56×56 container — logo or initials fallback */}
            <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#1a2235] border border-gray-700/50 group-hover:border-teal-500/30 group-hover:shadow-[0_0_16px_rgba(20,184,166,0.25)] transition-all duration-300 shrink-0 flex items-center justify-center">
              {showLogo ? (
                <img
                  src={logoUrl}
                  alt={`${tool.name} logo`}
                  width={40}
                  height={40}
                  className="w-10 h-10 object-contain"
                  loading="lazy"
                  onError={handleLogoError}
                />
              ) : (
                <span className="text-sm font-bold text-gray-300 select-none tracking-wide">
                  {getInitials(tool.name)}
                </span>
              )}
            </div>

            <h3 className="text-base font-bold text-white group-hover:text-teal-300 transition-colors duration-200 leading-snug">
              {tool.name}
            </h3>
          </div>

          {/* Category pill */}
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <span className="inline-block px-2.5 py-1 bg-teal-500/10 text-teal-400 text-xs font-semibold rounded-full border border-teal-500/25 whitespace-nowrap">
              {tool.category}
            </span>
          </div>

          {/* Description */}
          <p className="text-gray-400 text-sm leading-relaxed flex-1 mb-4">
            {tool.description}
          </p>

          {/* Footer row */}
          <div className="flex items-center justify-between gap-2 mt-auto">
            {pricingType ? (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${getPricingBadgeStyles(pricingType)}`}>
                {pricingType}
              </span>
            ) : <span />}
            <span className="px-4 py-2 bg-teal-500/20 hover:bg-teal-500 text-teal-300 hover:text-white text-sm font-semibold rounded-lg transition-all duration-300 text-center whitespace-nowrap cursor-pointer flex items-center gap-1.5 border border-teal-500/40 hover:border-teal-500 hover:shadow-lg hover:shadow-teal-500/30 active:scale-95">
              View Tool
              <i className="ri-arrow-right-line text-sm"></i>
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}
